import React, { useEffect, useState, useContext, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { formatIndianCurrency } from "../../helper/ConvertValue";
import { updateConventionalMode } from "../../redux/actions/TemplateTabs/actionCM";

import commoncss from "../../myfin_v2/css/common.module.css";
import buttoncss from "../../myfin_v2/css/button.module.css";
import tablecss from "../../myfin_v2/css/table.module.css";
import ReactIcon from "../../myfin_v2/css/table.module.css";
import { ReactComponent as BackIcon } from "../../myfin_v2/assets/icons/left_arrow.svg";

import { ThemeContext } from "../../helper/DarkModeContext";
import { Button } from "../../myfin_v2/elements/Button";
/**
 * GroupingDrilldownTable
 *
 * Props:
 *  - grouping: array (grouping structure)
 *  - onDrop: function(firstDroppedItem, targetLevel3Row, updatedGrouping) -> void
 *
 * Behavior:
 *  - Accepts drops only on level-3 rows.
 *  - Reads dropped payload (tries application/json, text/plain, then global fallback).
 *  - Extracts cy/py (or currentYear/previousYear variants) and updates the specific level-3 row.
 *  - Calls onDrop(...) after updating local state so the parent can persist the change.
 */
const GroupingDrilldownTable = ({
  grouping = [],
  onDrop,
   view: externalView,
  setView: externalSetView,
  setUpdateTabs
}) => {
  const conventionModeData = useSelector(
    (state) => state.ConventionModeReducer
  );
  const dispatch = useDispatch();

  const { theme } = useContext(ThemeContext);
  // internal editable copy of grouping
  const [data, setData] = useState(() => {
    const saved = sessionStorage.getItem("groupingData");
    return saved ? JSON.parse(saved) : (grouping || []);
  });
  
  // Internal view state - independent from parent
  const [internalView, setInternalView] = useState(() => {
    const saved = sessionStorage.getItem("view");
    return saved
      ? JSON.parse(saved)
      : { type: "root", level1: null, level2: null };
  });
  
  // Use internal view if available, otherwise use external
  const view = internalView ?? externalView ?? { type: "root", level1: null, level2: null };
  
  const setView = (newView) => {
    setInternalView(newView);
    if (externalSetView) {
      externalSetView(newView);
    }
  };
  
  const [updatedL2, setUpdatedL2] = useState([]);
  
  // Scroll position and highlight state
  const [scrollPosition, setScrollPosition] = useState(0);
  const [highlightedNoteId, setHighlightedNoteId] = useState(null);
  const tableContainerRef = useRef(null);
  const highlightedRowRef = useRef(null);

 useEffect(() => {
  const saved = sessionStorage.getItem("groupingData");

  if (saved) {
    setData(JSON.parse(saved)); // ✅ use dropped data
  } else {
    setData(grouping || []);    // fallback
  }
}, [grouping]);


  useEffect(() => {
    const savedView = sessionStorage.getItem("view");
    if (savedView) {
      try {
        setInternalView(JSON.parse(savedView));
      } catch (err) {
        console.error("Error parsing saved view:", err);
      }
    }
  }, []);

  const { level1, level2 } = view;

  useEffect(() => {
    if (level2) {
      const foundL2 = data
        .find((l1) => l1._id === level1?._id)
        ?.subgroup?.find((s) => s._id === level2?._id);
      
      // Always update to the found L2, or keep level2 if not found
      setUpdatedL2(foundL2 || level2);
    } else {
      setUpdatedL2([]);
    }
  }, [data, level1, level2]);

  // useEffect(() => {
  //   if (selectedL1 && selectedL2) {
  //     setView({ type: "detail", level1: selectedL1, level2: selectedL2 });
  //   } else {
  //     setView({
  //       type: "root",
  //       level1: null,
  //       level2: null,
  //     });
  //   }
  // }, [selectedL1, selectedL2]);


  // parse payload helper
  const getParsedPayloadFromEvent = (e) => {
    try {
      const json = e.dataTransfer.getData("application/json");
      if (json) {
        try {
          return JSON.parse(json);
        } catch {
          return json;
        }
      }
    } catch (err) {
      // ignore
    }
    try {
      const txt = e.dataTransfer.getData("text/plain");
      if (txt) {
        try {
          return JSON.parse(txt);
        } catch {
          return txt;
        }
      }
    } catch (err) {
      // ignore
    }
    // fallback for your global approach (if used)
    if (typeof window !== "undefined" && window.__MY_APP_DRAG_PAYLOAD) {
      return window.__MY_APP_DRAG_PAYLOAD;
    }
    return null;
  };

  // flexible extractor for cy/py fields
  const extractCyPyFromItem = (item) => {
    if (!item) return { cy: null, py: null };
    if (typeof item.cy !== "undefined" || typeof item.py !== "undefined") {
      return { cy: item.cy ?? null, py: item.py ?? null };
    }
    if (
      typeof item.currentYear !== "undefined" ||
      typeof item.previousYear !== "undefined"
    ) {
      return { cy: item.currentYear ?? null, py: item.previousYear ?? null };
    }
    if (
      typeof item.current_year !== "undefined" ||
      typeof item.previous_year !== "undefined"
    ) {
      return { cy: item.current_year ?? null, py: item.previous_year ?? null };
    }
    return { cy: null, py: null };
  };

  const [hoveredL3Id, setHoveredL3Id] = useState(null);
  // Helper to find updatedL2 (so updated values are reflected when drilling down)

  // const handleNoteClick = (level1, level2) => {
  //   // Save the current scroll position before navigating
  //   if (tableContainerRef.current) {
  //     setScrollPosition(tableContainerRef.current.scrollTop);
  //   }
  //   setHighlightedNoteId(level2._id);
  //   setView({ type: "detail", level1, level2 });
  // };

  const handleNoteClick = (level1, level2) => {
    // Save the current scroll position before navigating
    if (tableContainerRef.current) {
      sessionStorage.setItem("scrollPosition", tableContainerRef.current.scrollTop);
    }
    const newView = { type: "detail", level1, level2 };
    sessionStorage.setItem("highlightedNoteId", level2._id);
    sessionStorage.setItem("view", JSON.stringify(newView));
    setHighlightedNoteId(level2._id);
    setView(newView);
  };

  const handleBackToRoot = () => {
    const savedHighlightId = sessionStorage.getItem("highlightedNoteId");
    sessionStorage.removeItem("groupingData");
    sessionStorage.removeItem("highlightedNoteId");
    setHighlightedNoteId(savedHighlightId);
    setView({ type: "root", level1: null, level2: null });
    
    // Restore scroll position after state updates
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
      if (highlightedRowRef.current) {
        highlightedRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 0);
  };

  // drop handlers
  const onLevel3DragOver = (e) => {
    e.preventDefault(); // necessary to allow drop
  };

  const onLevel3Drop = (l3Row) => async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const parsed = getParsedPayloadFromEvent(e);
      if (!parsed) {
        console.warn(
          "Drop received but no parsed payload found (dataTransfer empty)."
        );
        return;
      }

      // resolve the first item from common payload shapes
      let firstItem = null;
      if (Array.isArray(parsed)) firstItem = parsed[0];
      else if (parsed.items && Array.isArray(parsed.items))
        firstItem = parsed.items[0];
      else if (parsed.item) firstItem = parsed.item;
      else firstItem = parsed;

      const { cy, py } = extractCyPyFromItem(firstItem);
      if (cy == null && py == null) {
        console.warn("Dropped payload didn't include cy/py values to map.", {
          parsed,
          firstItem,
        });
        return;
      }

      // immutably update the specific level-3 row's currentYear and previousYear
      const updatedGrouping = data.map((l1) => {
        if (!Array.isArray(l1.subgroup)) return l1;
        return {
          ...l1,
          subgroup: l1.subgroup.map((l2) => {
            if (!Array.isArray(l2.subgroup)) return l2;
            return {
              ...l2,
              subgroup: l2.subgroup.map((l3) => {
                if (l3._id === l3Row._id) {
                  const newL3 = {
                    ...l3,
                    currentYear: cy != null ? cy : l3.currentYear,
                    previousYear: py != null ? py : l3.previousYear,
                  };
                  console.log("Level-3 update:", {
                    before: l3,
                    after: newL3,
                    droppedPayload: firstItem,
                  });
                  const dragItem = firstItem?.item_;
                  dragItem["map_data"] = {
                    cy_map: {
                      fs_grp: l1?.name,
                      note_grp: l2.name,
                      note_no: l2?.notes,
                      sub_grp: l3.name,
                    },
                    py_map: {
                      fs_grp: l1?.name,
                      note_grp: l2.name,
                      note_no: l2?.notes,
                      sub_grp: l3.name,
                    },
                  };
                  const updatePayload = dragItem;

                  dispatch(
                    updateConventionalMode(
                      conventionModeData.set_payload,
                      updatePayload
                    )
                  );

                  return newL3;
                }
                return l3;
              }),
            };
          }),
        };
      });

      // update local state
      setData(updatedGrouping);
      // Persist to sessionStorage - IMPORTANT: keep view state alive
      sessionStorage.setItem("groupingData", JSON.stringify(updatedGrouping));
      sessionStorage.setItem("view", JSON.stringify(view));
      setUpdateTabs?.(prev => !prev);
      setView({ ...view }); 
      // Update the visible L2 data with the new changes
      const updatedL2Item = updatedGrouping
        .find(l1 => l1._id === view.level1?._id)
        ?.subgroup?.find(l2 => l2._id === view.level2?._id);
      
      if (updatedL2Item) {
        setUpdatedL2(updatedL2Item);
      }

      // call parent callback so the parent can persist the change (redux/api)
      if (typeof onDrop === "function") {
        try {
          onDrop(firstItem, l3Row, updatedGrouping);
        } catch (err) {
          console.error("onDrop callback threw an error:", err);
        }
      }
    } catch (error) {
      console.error("Error during drop operation:", error);
      // Don't exit view on error - keep user in detail view
    }
  };

  const onLevel3DragEnter = (id) => (e) => {
    e.preventDefault();
    setHoveredL3Id(id);
  };
  const onLevel3DragLeave = (id) => (e) => {
    e.preventDefault();
    setHoveredL3Id((curr) => (curr === id ? null : curr));
  };

  const logReorderContext = ({
    parent,
    child,
    fromIndex,
    toIndex,
    fullPath,
  }) => {
    console.log("update sequence:", {
      previous: {
        index: fromIndex,
        parentId: parent._id,
        parentName: parent.name,
      },
      current: {
        index: toIndex,
        parentId: parent._id,
        parentName: parent.name,
      },
      movedItem: {
        childId: child._id,
        childName: child.name,
      },
      hierarchy: fullPath, // full ancestry
    });
  };

  const moveItem = (arr, fromIndex, toIndex) => {
    const copy = [...arr];
    const [moved] = copy.splice(fromIndex, 1);
    copy.splice(toIndex, 0, moved);
    return copy;
  };

  const handleReorderL2 = (l1Id, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    const updated = data.map((l1) => {
      if (l1._id !== l1Id) return l1;

      const reorderedSubgroup = moveItem(l1.subgroup || [], fromIndex, toIndex);

      logReorderContext({
        parent: l1,
        child: l1.subgroup[fromIndex],
        fromIndex,
        toIndex,
        fullPath: [
          {
            level: 1,
            id: l1._id,
            name: l1.name,
          },
          {
            level: 2,
            id: l1.subgroup[fromIndex]._id,
            name: l1.subgroup[fromIndex].name,
          },
        ],
      });

      return {
        ...l1,
        subgroup: reorderedSubgroup,
      };
    });

    setData(updated);
    // Persist to sessionStorage
    sessionStorage.setItem("groupingData", JSON.stringify(updated));
  };

  // Render: Root view
  if (view.type === "root") {
    return (
      <div
        ref={tableContainerRef}
        style={{ height: "100%", overflowY: "auto" }}
      >
        <table
          className={tablecss.customTable}
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead>
            <tr>
              <th className={`text-left pl-3 ${commoncss.highlightRow}`}>
                Particulars
              </th>
              <th className={`text-left pl-3 ${commoncss.highlightRow}`}>
                Note
              </th>
              <th className={`text-right pl-3 ${commoncss.highlightRow}`}>
                Current Year
              </th>
              <th className={`text-right pl-3 ${commoncss.highlightRow}`}>
                Previous Year
              </th>
            </tr>
          </thead>
          <tbody
            style={{
              background: `${theme ? "" : "#363535"}`,
              color: `${theme ? "" : "#fff"}`,
            }}
          >
            {data.map((l1) => (
              <React.Fragment key={l1._id}>
                <tr className={`text-left pl-3 ${commoncss.highlightRow}`}>
                  <td
                    colSpan={4}
                    className={`text-left pl-3 ${commoncss.highlightRow} ${commoncss.fs14} ${commoncss.fw600}`}
                    style={{
                      padding: "8px",
                      borderBottom: "0.5px solid transparent",
                    }}
                  >
                    {l1.name}
                  </td>
                </tr>

                {(l1.subgroup || []).map((l2, l2Index) => (
                  <tr
                  className={commoncss.dataGrab}
                    key={l2._id}
                    draggable
                    // style={{ cursor: "grab" }}
                    onDragStart={(e) => {
                      // e.currentTarget.style.cursor = "grabbing";
                      e.dataTransfer.setData(
                        "text/plain",
                        JSON.stringify({
                          l1Id: l1._id,
                          fromIndex: l2Index,
                        })
                      );
                    }}
                    // onDragEnd={(e) => {
                    //   e.currentTarget.style.cursor = "grab";
                    // }}
                    onDragOver={(e) => {e.preventDefault()}}
                    onDrop={(e) => {
                      e.preventDefault();
                      const payload = JSON.parse(
                        e.dataTransfer.getData("text/plain")
                      );
                      if (!payload) return;

                      handleReorderL2(payload.l1Id, payload.fromIndex, l2Index);
                    }}
                  >
                    <td
                      className="text-left"
                      style={{
                        borderBottom: "0.5px solid transparent",
                        padding: "8px",
                      }}
                    >
                      {l2.name}
                    </td>
                    <td
                      ref={highlightedNoteId === l2._id ? highlightedRowRef : null}
                      style={{
                        borderBottom: "0.5px solid transparent",
                        padding: "8px",
                        cursor: "pointer",
                        textDecoration: "underline",
                        textAlign: "center",
                        backgroundColor: highlightedNoteId === l2._id ? "transparent" : "transparent",
                        transition: "background-color 0.3s ease",
                      }}
                      onClick={() => handleNoteClick(l1, l2)}
                    >
                      {l2.notes ?? ""}
                    </td>
                    <td
                      className="text-right"
                      style={{
                        borderBottom: "0.5px solid transparent",
                        padding: "8px",
                      }}
                    >
                      {formatIndianCurrency(l2.currentYear ?? 0)}
                    </td>
                    <td
                      className="text-right"
                      style={{
                        borderBottom: "0.5px solid transparent",
                        padding: "8px",
                      }}
                    >
                      {formatIndianCurrency(l2.previousYear ?? 0)}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Render: Detail view (level2 + level3)
  return (
    <div>
      <Button
        btnColor={buttoncss.primary}
        label={"Back"}
        onClick={handleBackToRoot}
        // icon={<BackIcon />}
      />

      <table
        className={tablecss.customTable}
        style={{ width: "100%", borderCollapse: "collapse" }}
      >
        <thead>
          <tr>
            <th className={`text-left pl-3 ${commoncss.highlightRow}`}>
              Line item
            </th>
            <th className={`text-right pl-3 ${commoncss.highlightRow}`}>
              Current Year
            </th>
            <th className={`text-right pl-3 ${commoncss.highlightRow}`}>
              Previous Year
            </th>
          </tr>
        </thead>
        <tbody
          style={{
            background: `${theme ? "" : "#363535"}`,
            color: `${theme ? "" : "#fff"}`,
          }}
        >
          <tr className={`text-left pl-3 ${commoncss.highlightRow}`}>
            <td
              colSpan={3}
              className={`text-left pl-3 ${commoncss.highlightRow} ${commoncss.fs14} ${commoncss.fw600}`}
              style={{
                padding: "8px",
                borderBottom: "0.5px solid transparent",
              }}
            >
              {`${updatedL2?.name ?? level2?.name}`}
            </td>
          </tr>

          {(updatedL2?.subgroup || []).map((l3) => {
            const isHovered = hoveredL3Id === l3._id;
            return (
              <tr
                key={l3._id}
                onDragOver={onLevel3DragOver}
                onDrop={onLevel3Drop(l3)}
                onDragEnter={onLevel3DragEnter(l3._id)}
                onDragLeave={onLevel3DragLeave(l3._id)}
                style={{
                  backgroundColor: isHovered ? "#e6f7ff" : "transparent",
                }}
              >
                <td
                  className="text-left"
                  style={{
                    borderBottom: "0.5px solid transparent",
                    padding: "8px",
                  }}
                >
                  {l3.name}
                </td>
                <td
                  className="text-right"
                  style={{
                    borderBottom: "0.5px solid transparent",
                    padding: "8px",
                  }}
                >
                  {formatIndianCurrency(l3.currentYear ?? 0)}
                </td>
                <td
                  className="text-right"
                  style={{
                    borderBottom: "0.5px solid transparent",
                    padding: "8px",
                  }}
                >
                  {formatIndianCurrency(l3.previousYear ?? 0)}
                </td>
              </tr>
            );
          })}

          {!updatedL2?.subgroup?.length && (
            <tr>
              <td
                colSpan={3}
                style={{
                  borderBottom: "0.5px solid transparent",
                  padding: "8px",
                }}
              >
                No Level 3 items.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default GroupingDrilldownTable;
